import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps a back-in-stock request pointed at whatever variant is selected right now. The form is
 * rendered once per page load, so without this the shop would be emailed about the variant that
 * happened to be selected when the page was built.
 *
 * @typedef {object} NotifyMeRefs
 * @property {HTMLInputElement} selection - Hidden field holding the requested option values.
 *
 * @extends {Component<NotifyMeRefs>}
 */
class NotifyMeComponent extends Component {
  requiredRefs = ['selection'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#updateSelection, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
  }

  #updateSelection = () => {
    const section = this.closest('.shopify-section, dialog') ?? document;
    const checked = /** @type {NodeListOf<HTMLInputElement>} */ (
      section.querySelectorAll('variant-picker input[type="radio"]:checked')
    );

    const selection = Array.from(checked)
      .map((input) => {
        const name = input.dataset.optionName;
        return name ? `${name}: ${input.value}` : input.value;
      })
      .join(', ');

    if (selection) this.refs.selection.value = selection;
  };
}

if (!customElements.get('notify-me-component')) {
  customElements.define('notify-me-component', NotifyMeComponent);
}
